import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { userConfigApi } from '../lib/api';
import { UserConfig } from '../lib/types';
import { Eye, EyeOff, Trash2, Edit, PlayCircle, FileText } from 'lucide-react';
import { UserConfigDialog } from '../components/config/UserConfigDialog';
import { TestTradeDialog } from '../components/config/TestTradeDialog';
import { LogsViewerDialog } from '../components/config/LogsViewerDialog';
import { TradingAPIInformation } from '../components/config/TradingAPIInformation';

export const Config: React.FC = () => {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<UserConfig | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [testTradeConfig, setTestTradeConfig] = useState<{ userId: string; apiKey: string } | null>(null);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  
  const apiUrl = import.meta.env.VITE_API_URL || 'Not configured';
  const tradeEndpoint = apiUrl === 'Not configured' ? 'Not configured' : `${apiUrl.replace(/\/+$/, '')}/trade`;

  const { data: configs = [], isLoading, error } = useQuery({
    queryKey: ['userConfigs'],
    queryFn: async () => {
      const response = await userConfigApi.getAll();
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: userConfigApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userConfigs'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UserConfig) => userConfigApi.update(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userConfigs'] });
      setIsDialogOpen(false);
      setSelectedConfig(null);
    },
  });

  const toggleSecretVisibility = (configId: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [configId]: !prev[configId]
    }));
  };

  const maskSecret = (secret: string) => {
    if (!secret) return '';
    return secret.substring(0, 4) + '•'.repeat(16);
  };

  const handleDelete = async (config: UserConfig) => {
    if (window.confirm(`Are you sure you want to delete configuration for "${config.name}"?`)) {
      deleteMutation.mutate(config.id);
    }
  };

  const handleEdit = (config: UserConfig) => {
    setSelectedConfig(config);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (data: UserConfig) => {
    await updateMutation.mutateAsync(data);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Trading Configurations</h1>
          <Button
            variant="secondary"
            onClick={() => setShowLogsDialog(true)}
          >
            <FileText className="h-4 w-4 mr-2" />
            View Debug Logs
          </Button>
        </div>

        {/* API Information Section */}
        <TradingAPIInformation apiUrl={apiUrl} tradeEndpoint={tradeEndpoint} />

        {error && (
          <div className="p-4 bg-red-50 text-red-700 rounded-md">
            Error loading configurations: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid gap-6">
            {configs.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No trading configurations found</p>
              </div>
            ) : (
              configs.map((config) => (
                <div key={config.id} className="bg-white shadow rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{config.name}</h3>
                      <p className="text-sm text-gray-500">ID: {config.id}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => toggleSecretVisibility(config.id)}
                      >
                        {showSecrets[config.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEdit(config)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(config)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-700">Testnet Configuration</h4>
                      <div className="space-y-2">
                        <div>
                          <label className="text-sm text-gray-600">Bybit Testnet API Key</label>
                          <p className="font-mono text-sm bg-gray-50 p-2 rounded">
                            {showSecrets[config.id] ? config.bybitTestnetApiKey : maskSecret(config.bybitTestnetApiKey)}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">Bybit Testnet API Secret</label>
                          <p className="font-mono text-sm bg-gray-50 p-2 rounded">
                            {showSecrets[config.id] ? config.bybitTestnetApiSecret : maskSecret(config.bybitTestnetApiSecret)}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">Trading Webhook Testnet API Key</label>
                          <div className="flex items-center gap-2">
                            <p className="font-mono text-sm bg-gray-50 p-2 rounded flex-1">
                              {showSecrets[config.id] ? config.testnetApiKey : maskSecret(config.testnetApiKey)}
                            </p>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setTestTradeConfig({ userId: config.id, apiKey: config.testnetApiKey })}
                              title="Test Trade"
                            >
                              <PlayCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-700">Production Configuration</h4>
                      <div className="space-y-2">
                        <div>
                          <label className="text-sm text-gray-600">Bybit Production API Key</label>
                          <p className="font-mono text-sm bg-gray-50 p-2 rounded">
                            {showSecrets[config.id] ? config.bybitProductionApiKey : maskSecret(config.bybitProductionApiKey)}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">Bybit Production API Secret</label>
                          <p className="font-mono text-sm bg-gray-50 p-2 rounded">
                            {showSecrets[config.id] ? config.bybitProductionApiSecret : maskSecret(config.bybitProductionApiSecret)}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">Trading Webhook Production API Key</label>
                          <p className="font-mono text-sm bg-gray-50 p-2 rounded">
                            {showSecrets[config.id] ? config.productionApiKey : maskSecret(config.productionApiKey)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {(config.proxyHost || config.proxyPort) && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="font-medium text-gray-700 mb-2">Proxy Configuration</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-600">Proxy Host</label>
                          <p className="font-mono text-sm bg-gray-50 p-2 rounded">
                            {config.proxyHost || 'Not configured'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">Proxy Port</label>
                          <p className="font-mono text-sm bg-gray-50 p-2 rounded">
                            {config.proxyPort || 'Not configured'}
                          </p>
                        </div>
                        {config.proxyUsername && (
                          <div>
                            <label className="text-sm text-gray-600">Proxy Username</label>
                            <p className="font-mono text-sm bg-gray-50 p-2 rounded">
                              {showSecrets[config.id] ? config.proxyUsername : maskSecret(config.proxyUsername)}
                            </p>
                          </div>
                        )}
                        {config.proxyPassword && (
                          <div>
                            <label className="text-sm text-gray-600">Proxy Password</label>
                            <p className="font-mono text-sm bg-gray-50 p-2 rounded">
                              {showSecrets[config.id] ? config.proxyPassword : maskSecret(config.proxyPassword)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {(config.longConfig || config.shortConfig) && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="font-medium text-gray-700 mb-2">Trading Strategies</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {config.longConfig && (
                          <div>
                            <label className="text-sm text-gray-600">Long Position Config</label>
                            <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                              {JSON.stringify(JSON.parse(config.longConfig), null, 2)}
                            </pre>
                          </div>
                        )}
                        {config.shortConfig && (
                          <div>
                            <label className="text-sm text-gray-600">Short Position Config</label>
                            <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                              {JSON.stringify(JSON.parse(config.shortConfig), null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        <UserConfigDialog
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setSelectedConfig(null);
          }}
          config={selectedConfig}
          onSubmit={handleSubmit}
        />

        {testTradeConfig && (
          <TestTradeDialog
            isOpen={!!testTradeConfig}
            onClose={() => setTestTradeConfig(null)}
            userId={testTradeConfig.userId}
            apiKey={testTradeConfig.apiKey}
          />
        )}
        
        <LogsViewerDialog
          isOpen={showLogsDialog}
          onClose={() => setShowLogsDialog(false)}
        />
      </div>
    </Layout>
  );
};